import { useLayoutEffect, useMemo } from "react";
import { useNavigation } from "expo-router";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { useCameras } from "@src/services/api/hooks";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { resolveCameraName } from "@src/features/cameras/utils/camera-display-name";
import { getCameraKeyString } from "@src/utils/key-parser";
import { showErrorToast } from "@src/utils/toast";
import { useInheritedAccessReason } from "@src/features/auth/hooks/useInheritedAccessReason";
import { track } from "@visioni/a3s-analytics";
import { useTenantId } from "@src/features/tenants";
import { requestErrorType } from "@src/services/api/error-type";
import {
  NotificationPhoneNumberFailed,
  NotificationPhoneNumberSaved,
  NotificationPreferenceFailed,
  NotificationPreferenceSaved,
  type NotificationPreferenceChange,
} from "@src/features/notifications/analytics";
import {
  useNotificationPreferences,
  useContactPhoneNumber,
  DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
  type NotificationPreferencesFormSite,
  type CameraNotificationPreference,
  type NotificationCategory,
  type NotificationChannel,
  type NotificationChannelPreferences,
  type NotificationCategoryPreferences,
  type NotificationAccessBannerReason,
} from "@visioni/a3s-notifications/app";

function orChannels(
  a: NotificationChannelPreferences,
  b: NotificationChannelPreferences,
): NotificationChannelPreferences {
  return { email: a.email || b.email, sms: a.sms || b.sms, push: a.push || b.push };
}

/** Merges two scopes' preferences into "is this cell covered by either" — used to compute `inherited`. */
function orCategoryPreferences(
  a: NotificationCategoryPreferences,
  b: NotificationCategoryPreferences,
): NotificationCategoryPreferences {
  return {
    alarmEvent: orChannels(a.alarmEvent, b.alarmEvent),
    cameraConfigChange: orChannels(a.cameraConfigChange, b.cameraConfigChange),
  };
}

export function useNotificationPreferencesPage() {
  const navigation = useNavigation();
  const { headerLeft } = useHeaderBackButton();
  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Notification Preferences",
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
    });
  }, [navigation, headerLeft]);

  const {
    preferences,
    isLoading: isPreferencesLoading,
    isRefreshing: isPreferencesRefreshing,
    isError: isPreferencesError,
    setCategory,
  } = useNotificationPreferences();
  const {
    phoneNumber,
    setPhoneNumber,
    isLoading: isPhoneNumberLoading,
    isRefreshing: isPhoneNumberRefreshing,
  } = useContactPhoneNumber();
  // isLoading is combined: the phone field is part of this same form, so it
  // must not become interactive (and empty) before its own, separately-
  // fetched value has loaded — otherwise a focus+blur on it while still
  // loading reads as "the user cleared it" and deletes the real, not-yet-
  // displayed number.
  //
  // isError is deliberately NOT combined: a phone-number fetch failure
  // (e.g. Auth0 wiring not yet deployed to this environment) must not fail
  // the whole preferences page — the field just renders empty, same as
  // "never set one", and the rest of the form keeps working.
  const isLoading = isPreferencesLoading || isPhoneNumberLoading;
  // The form shows the cached copy straight away but stays disabled until the
  // fresh one lands, so no edit is made against stale values.
  const isRefreshing = isPreferencesRefreshing || isPhoneNumberRefreshing;
  const isError = isPreferencesError;
  const { data: cameraList } = useCameras();
  const siteNames = useSiteNames();

  const siteGroups = useMemo(() => groupCamerasBySite(cameraList ?? [], "site", siteNames), [cameraList, siteNames]);

  const allCameras = useMemo(() => {
    const item = preferences.find((p) => p.scope.kind === "allCameras");
    return item?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES;
  }, [preferences]);

  const sites = useMemo<NotificationPreferencesFormSite[]>(
    () =>
      siteGroups.map((group) => {
        const item = preferences.find((p) => p.scope.kind === "site" && p.scope.siteId === group.siteId);
        return {
          siteKey: group.siteId,
          label: group.siteName,
          preferences: item?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
          // Nothing is broader than "All Cameras" — a site's Email is already
          // covered the moment "All Cameras" Email is on.
          inherited: allCameras,
        };
      }),
    [siteGroups, preferences, allCameras],
  );

  const cameras = useMemo<CameraNotificationPreference[]>(
    () =>
      (cameraList ?? []).map((camera) => {
        const cameraKey = getCameraKeyString(camera);
        const item = preferences.find(
          (p) =>
            p.scope.kind === "camera" &&
            p.scope.siteId === camera.site.id &&
            p.scope.assetId === camera.key.asset &&
            p.scope.cameraId === camera.key.id,
        );
        const siteItem = preferences.find((p) => p.scope.kind === "site" && p.scope.siteId === camera.site.id);
        return {
          cameraKey,
          label: resolveCameraName(camera, cameraKey),
          preferences: item?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
          // A camera is covered once either its own site or "All Cameras" is on
          // for that cell — the site's own value (not the site's already-merged
          // `inherited` display value) is enough, since "All Cameras" is OR'd in
          // here directly too.
          inherited: orCategoryPreferences(
            allCameras,
            siteItem?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
          ),
        };
      }),
    [cameraList, preferences, allCameras],
  );

  const handleToggleError = () => {
    showErrorToast({ message: "Couldn't save that change. Please try again." });
  };

  const tenantId = useTenantId();

  /** Tracks a preference save once it settles; a failure also shows the error toast. */
  const trackPreferenceSave = (save: Promise<void>, change: NotificationPreferenceChange) => {
    void save.then(
      () => track(NotificationPreferenceSaved, change),
      (error: unknown) => {
        track(NotificationPreferenceFailed, { ...change, error_type: requestErrorType(error) });
        handleToggleError();
      },
    );
  };

  const onToggleAllCamerasChannel = (
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => {
    trackPreferenceSave(setCategory({ kind: "allCameras" }, category, channel, enabled), {
      tenant_id: tenantId || undefined,
      level: "all_cameras",
      category,
      channel,
      enabled,
    });
  };

  const onToggleSiteChannel = (
    siteKey: string,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => {
    trackPreferenceSave(setCategory({ kind: "site", siteId: siteKey }, category, channel, enabled), {
      tenant_id: tenantId || undefined,
      level: "site",
      site_id: siteKey,
      category,
      channel,
      enabled,
    });
  };

  const onToggleCameraChannel = (
    cameraKey: string,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => {
    const camera = (cameraList ?? []).find((c) => getCameraKeyString(c) === cameraKey);
    if (!camera) return;
    trackPreferenceSave(
      setCategory(
        { kind: "camera", siteId: camera.site.id, assetId: camera.key.asset, cameraId: camera.key.id },
        category,
        channel,
        enabled,
      ),
      {
        tenant_id: tenantId || undefined,
        level: "camera",
        site_id: camera.site.id,
        camera_key: cameraKey,
        category,
        channel,
        enabled,
      },
    );
  };

  /** Returns the save itself (still rejecting on failure) so the field can let the user retry. */
  const onSavePhoneNumber = (phone: string | null): Promise<void> => {
    const change = { tenant_id: tenantId || undefined, action: phone ? "set" : "removed" } as const;
    const save = setPhoneNumber(phone);
    void save.then(
      () => track(NotificationPhoneNumberSaved, change),
      (error: unknown) => {
        track(NotificationPhoneNumberFailed, { ...change, error_type: requestErrorType(error) });
        handleToggleError();
      },
    );
    return save;
  };

  // Access from the support role or a Microsoft group is granted per request
  // and never stored, so notifications can't reach it.
  const accessNoticeReason: NotificationAccessBannerReason | null = useInheritedAccessReason();

  return {
    isLoading,
    isRefreshing,
    isError,
    allCameras,
    sites,
    cameras,
    onToggleAllCamerasChannel,
    onToggleSiteChannel,
    onToggleCameraChannel,
    phoneNumber,
    onSavePhoneNumber,
    accessNoticeReason,
  };
}
